import React from "react";
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  useTheme,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import transcribeLogo from "../../images/tranzcribe-logo-text-black.png";
import { useAuth, useUser, useClerk } from "@clerk/clerk-react";
import { useSelector } from "react-redux";

const Header: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { isSignedIn } = useAuth();
  const { user } = useUser();
  const { signOut } = useClerk();
  const userData = useSelector((state: any) => state.user.userData);
  
  // Menu state for user avatar dropdown
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [logoutDialogOpen, setLogoutDialogOpen] = React.useState(false);
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);
  
  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };
  
  const handleGoToDashboard = () => {
    handleCloseMenu();
    navigate("/dashboard");
  };
  
  const handleLogout = () => {
    handleCloseMenu();
    setLogoutDialogOpen(true);
  };

  const handleConfirmLogout = async () => {
    setIsLoggingOut(true);
    try {
      // Logout with Clerk
      await signOut();
      navigate("/");
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoggingOut(false);
      setLogoutDialogOpen(false);
    }
  };

  const handleCancelLogout = () => {
    setLogoutDialogOpen(false);
  };

  const handleMobileMenuOpen = () => {
    setMobileMenuOpen(true);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const handleMobileMenuNavigate = (path: string) => {
    setMobileMenuOpen(false);
    navigate(path);
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (userData?.firstName && userData?.lastName) {
      return `${userData.firstName.charAt(0)}${userData.lastName.charAt(0)}`.toUpperCase();
    } else if (user?.firstName && user?.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
    }
    return user?.emailAddresses?.[0]?.emailAddress?.charAt(0).toUpperCase() || "U";
  };

  return (
    <AppBar
      position="sticky"
      sx={{
        backgroundColor: "white",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        padding: { xs: 1, md: 2 },
        top: 0,
        zIndex: 1000,
      }}
    >
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: "80px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 6 }}>
          {/* Logo */}
          <Box 
            sx={{ 
              cursor: "pointer",
              display: "flex",
              alignItems: "center" 
            }}
            onClick={() => navigate("/")}
          >
            <img
              src={transcribeLogo}
              alt="Tranzcribe"
              style={{ height: "auto", width: "200px" }}
            />
          </Box>
          
          {/* Left-aligned Menu */}
          <Box 
            sx={{ 
              display: { xs: "none", md: "flex" },
              gap: 4
            }}
          >
            <Button
              variant="text"
              onClick={() => navigate("/start")}
              sx={{
                fontWeight: "500",
                textTransform: "none",
                color: "#333333",
                fontSize: "1rem",
                "&:hover": {
                  color: theme.palette.primary.main,
                  backgroundColor: "transparent"
                }
              }}
            >
              Pricing
            </Button>
            <Button
              variant="text"
              onClick={() => navigate("/contact")}
              sx={{
                fontWeight: "500",
                textTransform: "none",
                color: "#333333",
                fontSize: "1rem",
                "&:hover": {
                  color: theme.palette.primary.main,
                  backgroundColor: "transparent"
                }
              }}
            >
              Contact
            </Button>
            {isSignedIn && (
              <Button
                variant="text"
                onClick={() => navigate("/dashboard")}
                sx={{
                  fontWeight: "500",
                  textTransform: "none",
                  color: "#333333",
                  fontSize: "1rem",
                  "&:hover": {
                    color: theme.palette.primary.main,
                    backgroundColor: "transparent"
                  }
                }}
              >
                Dashboard
              </Button>
            )}
          </Box>
        </Box>

        {/* Right-aligned Buttons */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2
          }}
        >
          {/* Mobile buttons - only show on small screens */}
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              alignItems: 'center',
              gap: 1,
            }}
          >
            {!isSignedIn && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate("/start")}
                sx={{
                  fontWeight: "600",
                  textTransform: "none",
                  color: "white",
                  fontSize: "0.9rem",
                  borderRadius: "6px",
                  padding: "6px 12px",
                  boxShadow: "0 4px 12px rgba(25, 118, 210, 0.15)",
                }}
              >
                Try for Free
              </Button>
            )}
            
            <IconButton
              onClick={handleMobileMenuOpen}
              sx={{
                color: '#333333',
                p: 1,
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>

          {/* Desktop buttons */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
            {isSignedIn ? (
              <>
                <Avatar 
                  sx={{ 
                    cursor: 'pointer',
                    bgcolor: theme.palette.primary.main,
                    color: 'white',
                    fontWeight: 'bold',
                    '&:hover': { opacity: 0.9 }
                  }}
                  onClick={handleOpenMenu}
                >
                  {getUserInitials()}
                </Avatar>
                <Menu
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleCloseMenu}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  <MenuItem onClick={handleGoToDashboard}>Dashboard</MenuItem>
                  <Divider />
                  <MenuItem onClick={handleLogout}>Logout</MenuItem>
                </Menu>
              </>
            ) : (
              <>
                <Button
                  variant="text"
                  color="inherit"
                  onClick={() => navigate("/login")}
                  sx={{
                    fontWeight: "500",
                    textTransform: "none",
                    color: "#333333",
                    fontSize: "1rem",
                    "&:hover": {
                      color: theme.palette.primary.main,
                      backgroundColor: "transparent"
                    }
                  }}
                >
                  Login
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate("/start")}
                  sx={{
                    fontWeight: "600",
                    textTransform: "none",
                    color: "white",
                    fontSize: "1rem",
                    borderRadius: "6px",
                    padding: "8px 16px",
                    boxShadow: "0 4px 12px rgba(25, 118, 210, 0.15)",
                  }}
                >
                  Try for Free
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Toolbar>
      
      {/* Logout Confirmation Dialog */}
      <Dialog open={logoutDialogOpen} onClose={handleCancelLogout}>
        <DialogTitle>Confirm Logout</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to logout?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelLogout}>Cancel</Button>
          <Button onClick={handleConfirmLogout} autoFocus>
            Logout
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: 280,
            paddingTop: 2,
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Menu
          </Typography>
          <IconButton onClick={handleMobileMenuClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        
        <Divider />
        
        <List>
          {/* Login/Dashboard - First option */}
          {!isSignedIn ? (
            <ListItem disablePadding>
              <ListItemButton onClick={() => handleMobileMenuNavigate('/login')}>
                <ListItemText 
                  primary="Login" 
                  primaryTypographyProps={{ fontWeight: 600, color: 'primary.main' }}
                />
              </ListItemButton>
            </ListItem>
          ) : (
            <ListItem disablePadding>
              <ListItemButton onClick={() => handleMobileMenuNavigate('/dashboard')}>
                <ListItemText 
                  primary="Dashboard" 
                  primaryTypographyProps={{ fontWeight: 600, color: 'primary.main' }}
                />
              </ListItemButton>
            </ListItem>
          )}
          
          <Divider sx={{ my: 1 }} />
          
          {/* Other navigation items */}
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleMobileMenuNavigate('/start')}>
              <ListItemText primary="Pricing" />
            </ListItemButton>
          </ListItem>
          
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleMobileMenuNavigate('/contact')}>
              <ListItemText primary="Contact" />
            </ListItemButton>
          </ListItem>
          
          <Divider sx={{ my: 1 }} />
          
          {/* Legal pages */}
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleMobileMenuNavigate('/privacy-policy')}>
              <ListItemText primary="Privacy Policy" />
            </ListItemButton>
          </ListItem>
          
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleMobileMenuNavigate('/terms-of-service')}>
              <ListItemText primary="Terms of Service" />
            </ListItemButton>
          </ListItem>

          {/* Logout option for signed in users */}
          {isSignedIn && (
            <>
              <Divider sx={{ my: 1 }} />
              <ListItem disablePadding>
                <ListItemButton onClick={() => {
                  setMobileMenuOpen(false);
                  handleLogout();
                }}>
                  <ListItemText primary="Logout" />
                </ListItemButton>
              </ListItem>
            </>
          )}
        </List>
      </Drawer>
    </AppBar>
  );
};

export default Header; 